import axios from 'axios';
import { Member, CreateMemberDto, UpdateMemberDto, Group, Client, ApiResponse } from './types';

// Re-export types for convenience
export type { Member, CreateMemberDto, UpdateMemberDto, Group, Client, ApiResponse } from './types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Vlastní client_id webu. Nedá se přečíst z API klíče (žádný endpoint ho nevrací),
 * ale nese ho každá epocha, takže ho aplikace nastaví, jakmile epochy načte.
 * Používá se jen jako záloha — výslovná volba organizace má vždycky přednost.
 */
let fallbackClientId: number | null = null;

export function setFallbackClientId(id: number | null): void {
  fallbackClientId = id;
}

const CLIENT_KEY = 'selectedClientId';

/**
 * Vybraná organizace (jen pro admin klíč). Dřív se držela v sessionStorage, takže ji
 * zavření karty zahodilo — teď je v localStorage, aby volba přežila i restart prohlížeče.
 * Čte se synchronně při načtení modulu, aby ji nesl už úplně první požadavek.
 */
let selectedClientId: string | null = (() => {
  try {
    const stored = localStorage.getItem(CLIENT_KEY);
    if (stored) return stored;
    // přenes volbu z původního umístění, ať o ni nikdo nepřijde
    const legacy = sessionStorage.getItem(CLIENT_KEY);
    if (legacy) {
      localStorage.setItem(CLIENT_KEY, legacy);
      sessionStorage.removeItem(CLIENT_KEY);
      return legacy;
    }
  } catch {
    /* privátní režim může úložiště zakázat — pak se jede bez zapamatování */
  }
  return null;
})();

/**
 * Volba je externí store, na který se React přihlašuje přes useSyncExternalStore.
 * Bez toho by se komponenty po přepnutí organizace nepřekreslily a `Select` by
 * skočil zpátky na starou hodnotu.
 */
const clientListeners = new Set<() => void>();

export function subscribeSelectedClientId(listener: () => void): () => void {
  clientListeners.add(listener);
  return () => clientListeners.delete(listener);
}

export function getSelectedClientId(): string | null {
  return selectedClientId;
}

export function setSelectedClientId(id: string | null): void {
  if (id === selectedClientId) return;
  selectedClientId = id;
  try {
    if (id) localStorage.setItem(CLIENT_KEY, id);
    else localStorage.removeItem(CLIENT_KEY);
  } catch {
    /* viz výše */
  }
  clientListeners.forEach((listener) => listener());
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication token from Cloudflare Access
api.interceptors.request.use((config) => {
  // For production with API key:
  const apiKey = import.meta.env.VITE_API_KEY;
  
  if (apiKey) {
    config.headers['X-API-Key'] = apiKey;
  }

  // Routy /api/pairing/* běží mimo auth middleware, takže si klienta neumí odvodit
  // z API klíče a vyžadují hlavičku X-Client-Id. Admin ji má z přepínače organizací,
  // běžný klientský klíč ji nemá — proto fallback zjištěný z dat (viz setFallbackClientId).
  const clientId = selectedClientId ?? fallbackClientId;
  if (clientId) {
    config.headers['X-Client-Id'] = String(clientId);
  }

  return config;
});

// Add response interceptor for better error messages
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('[API] Authentication failed - check API key');
    }
    return Promise.reject(error);
  }
);

// Types for games
export interface Game {
  id: number;
  uuid: string;
  name: string;
  game_type_id?: string;
  epoch_id?: number;
  created_at: string;
  ended_at: string | null;
  has_internal_mrtvoliste?: number;
}

export interface Score {
  member_number: number;
  name: string | null;
  group_name: string | null;
  group_color: string | null;
  total_minutes: number;
  death_count: number;
  games_played?: number;
}

export interface StopkyGroupResult {
  groupId: number;
  groupName: string;
  groupColor: string;
  elapsedMilliseconds: number;
  updatedAt: string;
}

export interface StopkyDeviceResult {
  deviceClientId: string;
  deviceName: string;
  groups: StopkyGroupResult[];
}

export interface HostinaLeaderboardEntry {
  groupId: number;
  groupName: string;
  groupColor: string;
  totalPoints: number;
  scannedCount: number;
  memberCount: number;
}

export interface DominationsTerritory {
  deviceId: string;
  currentState: 'neutral' | 'black' | 'white';
  blackScore: number;
  whiteScore: number;
  lastUpdatedAt: string;
}

export interface DominationsScore {
  totalBlackScore: number;
  totalWhiteScore: number;
  territories: DominationsTerritory[];
}

export interface PocitadloLeaderboardEntry {
  group_id: number;
  group_name: string;
  group_color: string;
  total_points: number;
  transaction_count: number;
  member_count: number;
}

export interface PocitadloMemberEntry {
  member_id: number;
  member_name: string;
  member_number: number | null;
  group_id: number;
  group_name: string;
  group_color: string;
  total_points: number;
  transaction_count: number;
}

export interface WalkieLeaderboardEntry {
  group_id: number;
  group_name: string;
  group_color: string;
  total_points: number;
  calls_won: number;
}

export interface WalkieStationInfo {
  id: number;
  position: number;
  name: string | null;
  points: number;
}

export interface WalkieRoundRecord {
  round_number: number;
  started_at: string;
  completed_at: string | null;
  calls: Array<{
    station_id: number;
    group_id: number | null;
    group_name: string | null;
    group_color: string | null;
    points: number;
    recorded_at: string;
  }>;
}

export interface WalkieRoundsHistory {
  stations: WalkieStationInfo[];
  rounds: WalkieRoundRecord[];
  leaderboard: WalkieLeaderboardEntry[];
}

export const membersApi = {
  /**
   * Bez `epochId` vrací členy aktivní epochy. S ním jinou epochu — to potřebuje
   * kopírování členů mezi epochami. Server ověřuje, že epocha patří klientovi.
   */
  getAll: async (epochId?: number): Promise<Member[]> => {
    const response = await api.get<ApiResponse<Member[]>>('/members', {
      params: epochId ? { epoch_id: epochId } : undefined,
    });
    return response.data.data || [];
  },

  getById: async (id: number): Promise<Member> => {
    const response = await api.get<ApiResponse<Member>>(`/members/${id}`);
    if (!response.data.data) {
      throw new Error('Member not found');
    }
    return response.data.data;
  },

  /**
   * Chybové odpovědi serveru mají 4xx/5xx, takže je axios odmítne sám. Když projde
   * 2xx, zápis se povedl — i kdyby v odpovědi chybělo `data`. Dřív se to hlásilo
   * jako "Failed to create member", přestože člen v databázi vznikl.
   */
  create: async (data: CreateMemberDto): Promise<Member | null> => {
    const response = await api.post<ApiResponse<Member>>('/members', data);
    return response.data.data ?? null;
  },

  update: async (id: number, data: UpdateMemberDto): Promise<Member | null> => {
    const response = await api.put<ApiResponse<Member>>(`/members/${id}`, data);
    return response.data.data ?? null;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/members/${id}`);
  },
};

export const gamesApi = {
  /**
   * Epocha je povinná schválně — bez ní server vrací hry ze všech epoch a přehledy
   * i statistiky by míchaly tábory dohromady.
   */
  getAll: async (epochId: number): Promise<Game[]> => {
    const response = await api.get<ApiResponse<Game[]>>('/games', {
      params: { epoch_id: epochId },
    });
    return response.data.data || [];
  },

  getById: async (uuid: string): Promise<Game> => {
    const response = await api.get<ApiResponse<Game>>(`/games/${uuid}`);
    if (!response.data.data) {
      throw new Error('Game not found');
    }
    return response.data.data;
  },

  create: async (name: string, uuid: string, gameTypeId?: string): Promise<Game> => {
    const response = await api.post<ApiResponse<Game>>('/games', { 
      name, 
      uuid,
      game_type_id: gameTypeId 
    });
    if (!response.data.data) {
      throw new Error(response.data.error || 'Failed to create game');
    }
    return response.data.data;
  },

  getScores: async (gameUuid: string): Promise<Score[]> => {
    const response = await api.get<ApiResponse<Score[]>>(`/games/${gameUuid}/scores`);
    return response.data.data || [];
  },

  /**
   * Epocha je povinná schválně: bez `epoch_id` server sčítá napříč všemi epochami
   * a statistika pak míchá tábory dohromady. Statistiky jsou vždycky per epocha.
   */
  getOverallScores: async (epochId: number): Promise<Score[]> => {
    const response = await api.get<ApiResponse<Score[]>>(
      `/games/scores/overall?epoch_id=${epochId}`
    );
    return response.data.data || [];
  },

  endGame: async (uuid: string): Promise<void> => {
    await api.put(`/games/${uuid}/end`);
  },

  setInternalMrtvoliste: async (uuid: string, enabled: boolean): Promise<Game> => {
    const response = await api.put<ApiResponse<Game>>(`/games/${uuid}/internal-mrtvoliste`, { enabled });
    if (!response.data.data) {
      throw new Error(response.data.error || 'Failed to update internal mrtvoliste');
    }
    return response.data.data;
  },

  recordDeath: async (gameUuid: string, memberNumber: number, deathMinutes: number): Promise<void> => {
    await api.post('/games/scores', {
      game_uuid: gameUuid,
      member_number: memberNumber,
      death_minutes: deathMinutes,
    });
  },

  getStopkyTimes: async (gameUuid: string): Promise<StopkyDeviceResult[]> => {
    const response = await api.get<ApiResponse<StopkyDeviceResult[]>>(`/games/${gameUuid}/stopky-times`);
    return response.data.data || [];
  },

  getHostinaLeaderboard: async (gameUuid: string): Promise<HostinaLeaderboardEntry[]> => {
    const response = await api.get<{ success: boolean; leaderboard: HostinaLeaderboardEntry[] }>(`/games/${gameUuid}/hostina/leaderboard`);
    return response.data.leaderboard || [];
  },

  getDominationsScore: async (gameUuid: string): Promise<DominationsScore> => {
    const response = await api.get<{ 
      success: boolean;
      totalBlackScore: number;
      totalWhiteScore: number;
      territories: DominationsTerritory[];
    }>(`/games/${gameUuid}/dominations/score`);
    return {
      totalBlackScore: response.data.totalBlackScore,
      totalWhiteScore: response.data.totalWhiteScore,
      territories: response.data.territories || [],
    };
  },

  getPocitadloLeaderboard: async (gameUuid: string): Promise<PocitadloLeaderboardEntry[]> => {
    const response = await api.get<PocitadloLeaderboardEntry[]>(`/games/${gameUuid}/pocitadlo/leaderboard`);
    return response.data || [];
  },

  getPocitadloMembersLeaderboard: async (gameUuid: string): Promise<PocitadloMemberEntry[]> => {
    const response = await api.get<PocitadloMemberEntry[]>(`/games/${gameUuid}/pocitadlo/members-leaderboard`);
    return response.data || [];
  },

  getWalkieRounds: async (gameUuid: string): Promise<WalkieRoundsHistory> => {
    const response = await api.get<WalkieRoundsHistory>(`/games/${gameUuid}/walkie/rounds`);
    return response.data;
  },

  getOverallMvpStats: async (): Promise<{
    strategy: string;
    overallVotes: Array<{
      memberNumber: number;
      memberName: string;
      groupName: string;
      groupColor: string;
      totalVotes: number;
      gamesVoted: number;
      position?: number;
    }>;
    groupStats: Array<{
      groupId: number;
      groupName: string;
      groupColor: string;
      totalVotesGiven: number;
      gamesParticipated: number;
    }>;
    groupReceivedStats: Array<{
      groupId: number;
      groupName: string;
      groupColor: string;
      totalVotesReceived: number;
    }>;
  }> => {
    const response = await api.get<{
      success: boolean;
      strategy: string;
      overallVotes: Array<{
        memberNumber: number;
        memberName: string;
        groupName: string;
        groupColor: string;
        totalVotes: number;
        gamesVoted: number;
        position?: number;
      }>;
      groupStats: Array<{
        groupId: number;
        groupName: string;
        groupColor: string;
        totalVotesGiven: number;
        gamesParticipated: number;
      }>;
      groupReceivedStats: Array<{
        groupId: number;
        groupName: string;
        groupColor: string;
        totalVotesReceived: number;
      }>;
    }>('/mvp-votes/overall');
    return {
      strategy: response.data.strategy || 'classic',
      overallVotes: response.data.overallVotes || [],
      groupStats: response.data.groupStats || [],
      groupReceivedStats: response.data.groupReceivedStats || [],
    };
  },
};

export const groupsApi = {
  getAll: async (): Promise<Group[]> => {
    const response = await api.get<ApiResponse<Group[]>>('/groups');
    return response.data.data || [];
  },

  getById: async (id: number): Promise<Group> => {
    const response = await api.get<ApiResponse<Group>>(`/groups/${id}`);
    if (!response.data.data) {
      throw new Error('Group not found');
    }
    return response.data.data;
  },

  // Backend bere i barvu (createGroupSchema v server/src/routes/groups.ts) — dřív se
  // posílalo jen jméno, takže barvu nešlo z webu nastavit.
  create: async (data: { name: string; color?: string }): Promise<Group> => {
    const response = await api.post<ApiResponse<Group>>('/groups', data);
    if (!response.data.data) {
      throw new Error(response.data.error || 'Failed to create group');
    }
    return response.data.data;
  },

  update: async (id: number, data: { name?: string; color?: string }): Promise<Group> => {
    const response = await api.put<ApiResponse<Group>>(`/groups/${id}`, data);
    if (!response.data.data) {
      throw new Error(response.data.error || 'Failed to update group');
    }
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/groups/${id}`);
  },

  getMembers: async (id: number): Promise<Member[]> => {
    const response = await api.get<ApiResponse<Member[]>>(`/groups/${id}/members`);
    return response.data.data || [];
  },
};

export const adminApi = {
  getClients: async (): Promise<Client[]> => {
    const response = await api.get<ApiResponse<Client[]>>('/admin/clients');
    return response.data.data || [];
  },

  deleteAllStats: async (): Promise<void> => {
    await api.delete('/admin/stats');
  },

  deleteGamesHistory: async (): Promise<void> => {
    await api.delete('/admin/games-history');
  },

  deleteAllGroups: async (): Promise<void> => {
    await api.delete('/admin/groups');
  },

  deleteAllMembers: async (): Promise<void> => {
    await api.delete('/admin/members');
  },
};

// ---------------------------------------------------------------------------
// Epochy, zařízení, herní módy, vysílačky
//
// Tvary odpovědí se mezi routami liší (někde `data`, jinde `epochs` nebo
// `gameTypes`) — obal to sjednocuje, aby komponenty řešily jen data.
// ---------------------------------------------------------------------------

export type MvpStrategy = 'none' | 'classic' | 'per_group';

export interface Epoch {
  id: number;
  name: string;
  client_id: number;
  is_active: number;
  mvp_strategy: MvpStrategy;
  created_at: string;
  updated_at: string;
}

export interface Device {
  id: number;
  deviceId: string;
  deviceName: string | null;
  pairedAt: string | null;
  deviceInfo: Record<string, unknown> | null;
  status: 'COMPLETED' | 'UNPAIRED' | 'PENDING';
  isAdmin: boolean;
}

export interface PairingSession {
  pairingCode: string;
  /** JSON, který se vykreslí do QR kódu a naskenuje ho mobil. */
  qrCodeData: string;
  expiresAt: string;
}

export interface GameType {
  id: string;
  name: string;
  description?: string | null;
  default_enabled: boolean;
  default_order: number;
  enabled?: boolean;
  display_order?: number;
}

/** Rozpad nabraných bodů po skupinách — počítá ho server. */
export interface WalkieStationBreakdown {
  group_id: number;
  group_name: string;
  group_color: string;
  points: number;
}

/**
 * Tvar odpovídá buildState() v server/src/routes/walkie.ts. Držitel stanoviště se
 * posílá jako `last_holder_group_id` — jméno a barvu skupiny si klient dohledá sám.
 */
export interface WalkieStation {
  id: number;
  position: number;
  name: string | null;
  points: number;
  last_call_group_id: number | null;
  last_call_round_number: number | null;
  has_last_call: boolean;
  last_holder_group_id: number | null;
  last_holder_round_number: number | null;
  breakdown: WalkieStationBreakdown[];
  none_count: number;
  current_call: { group_id: number | null; points: number; recorded_at: string } | null;
}

export interface WalkieRound {
  round_number: number;
  started_at: string;
  completed_at: string | null;
}

export interface WalkieState {
  settings: { round_interval_seconds: number; notifications_enabled: boolean };
  stations: WalkieStation[];
  leaderboard: WalkieLeaderboardEntry[];
  activeRound: WalkieRound | null;
  lastCompletedRound: WalkieRound | null;
  roundsCount: number;
}

export interface WalkieConfigDto {
  stations: Array<{ position: number; name: string | null; points: number }>;
  roundIntervalSeconds?: number;
  notificationsEnabled?: boolean;
}

export const epochsApi = {
  getAll: async (): Promise<Epoch[]> => {
    const r = await api.get<{ success: boolean; epochs: Epoch[] }>('/epochs');
    return r.data.epochs || [];
  },
  create: async (name: string, mvp_strategy: MvpStrategy): Promise<Epoch> => {
    const r = await api.post<{ success: boolean; epoch: Epoch }>('/epochs', { name, mvp_strategy });
    return r.data.epoch;
  },
  update: async (id: number, data: { name?: string; mvp_strategy?: MvpStrategy }): Promise<Epoch> => {
    const r = await api.put<{ success: boolean; epoch: Epoch }>(`/epochs/${id}`, data);
    return r.data.epoch;
  },
  activate: async (id: number): Promise<void> => {
    await api.put(`/epochs/${id}/activate`);
  },
  deactivateAll: async (): Promise<void> => {
    await api.put('/epochs/deactivate-all');
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/epochs/${id}`);
  },
};

export const devicesApi = {
  getAll: async (): Promise<Device[]> => {
    const r = await api.get<ApiResponse<Device[]>>('/pairing/devices/all');
    return r.data.data || [];
  },
  unpair: async (deviceId: string): Promise<void> => {
    await api.delete(`/pairing/devices/${deviceId}`);
  },

  /**
   * Admin zařízení smí měnit konfiguraci hry (u vysílaček třeba stanoviště a interval
   * kola) — viz requireWalkieConfigAccess v server/src/routes/walkie.ts.
   */
  setAdmin: async (deviceId: string, isAdmin: boolean): Promise<void> => {
    await api.patch(`/pairing/devices/${deviceId}/admin`, { is_admin: isAdmin });
  },
  /**
   * Původní DevicePairingModal volal natvrdo produkční URL, takže se z lokálního
   * vývoje párovalo proti produkci. Tady jde všechno přes sdílenou axios instanci.
   */
  initiatePairing: async (): Promise<PairingSession> => {
    const r = await api.post<ApiResponse<PairingSession>>('/pairing/initiate');
    if (!r.data.data) throw new Error(r.data.error || 'Nepodařilo se vytvořit párovací kód');
    return r.data.data;
  },

  /** Vrací true, jakmile se zařízení spáruje — modal se tím sám zavře. */
  checkPairingStatus: async (code: string): Promise<boolean> => {
    const r = await api.get<{ success: boolean; data?: { deviceId?: string } }>(
      `/pairing/status/${code}`
    );
    return Boolean(r.data?.data?.deviceId);
  },
};

export const gameTypesApi = {
  getAll: async (): Promise<GameType[]> => {
    const r = await api.get<{ gameTypes: GameType[] }>('/game-types');
    return r.data.gameTypes || [];
  },
  updatePreferences: async (
    preferences: Array<{ game_type_id: string; enabled: boolean; display_order: number }>
  ): Promise<void> => {
    await api.put('/game-types/preferences', { preferences });
  },
  resetPreferences: async (): Promise<void> => {
    await api.post('/game-types/reset-preferences');
  },
};

export const walkieApi = {
  getState: async (gameUuid: string): Promise<WalkieState> => {
    const r = await api.get<WalkieState>(`/games/${gameUuid}/walkie/state`);
    return r.data;
  },
  /**
   * Konfiguraci mění jen admin zařízení — web s klientským API klíčem backend
   * pouští vždycky (viz komentář u requireWalkieConfigAccess v server/src/routes/walkie.ts).
   */
  saveConfig: async (gameUuid: string, config: WalkieConfigDto): Promise<void> => {
    await api.put(`/games/${gameUuid}/walkie/config`, config);
  },
};

export interface GroupParticipation {
  id: number;
  name: string;
  color: string;
  is_active: number;
  member_count: number;
}

export const participationApi = {
  getGroups: async (gameUuid: string): Promise<GroupParticipation[]> => {
    const r = await api.get<{ success: boolean; groups: GroupParticipation[] }>(
      `/games/${gameUuid}/group-participation`
    );
    return r.data.groups || [];
  },
  setGroups: async (
    gameUuid: string,
    groups: Array<{ groupId: number; isActive: boolean }>
  ): Promise<void> => {
    await api.put(`/games/${gameUuid}/group-participation`, { groups });
  },
};

export interface PocitadloTransaction {
  id: number;
  points: number;
  recorded_by: string | null;
  recorded_at: string;
  group_id: number;
  group_name: string;
  group_color: string;
}

export interface MvpVote {
  memberNumber: number;
  memberName: string | null;
  groupName: string | null;
  groupColor: string | null;
  voteCount: number;
  position?: number;
}

export const gameExtrasApi = {
  getPocitadloHistory: async (gameUuid: string): Promise<PocitadloTransaction[]> => {
    const r = await api.get<PocitadloTransaction[]>(`/games/${gameUuid}/pocitadlo/history`);
    return Array.isArray(r.data) ? r.data : [];
  },
  getMvpVotes: async (
    gameUuid: string
  ): Promise<{ votes: MvpVote[]; strategy: MvpStrategy }> => {
    const r = await api.get<{ success: boolean; votes: MvpVote[]; strategy: MvpStrategy }>(
      `/games/${gameUuid}/mvp-votes`
    );
    return { votes: r.data.votes || [], strategy: r.data.strategy || 'none' };
  },
};

// Export api instance for direct use (e.g., in DevicesManagement)
export { api };
